export type DocMeta = {
  filename?: string;
  slug: string;
  title: string;
  subtitle?: string;
  category: string;
  searchable?: boolean;
};

export const categoryOrder = [
  'Getting Started',
  'Settings',
  'Transcription',
  'Enhancements',
  'Power Mode',
  'Dictionary',
  'Common Issues',
];

export const docsRegistry: DocMeta[] = [
  {
    filename: 'introduction',
    slug: 'introduction',
    title: 'Introduction',
    subtitle: 'A quick overview of what VoiceInk can do.',
    category: 'Getting Started',
  },
  {
    filename: 'installation',
    slug: 'installation',
    title: 'Installation',
    subtitle: 'Install VoiceInk and get your first transcription running.',
    category: 'Getting Started',
  },
  {
    slug: 'recommended-models',
    title: 'Recommended AI Models',
    subtitle: 'Model choices for fast transcription and enhancement.',
    category: 'Getting Started',
    searchable: false,
  },
  {
    filename: 'license-management',
    slug: 'license-management',
    title: 'License Management',
    subtitle: 'Manage purchases, devices, and activation.',
    category: 'Getting Started',
  },
  {
    filename: 'shortcuts',
    slug: 'shortcuts',
    title: 'Shortcuts',
    subtitle: 'Configure keyboard shortcuts for faster dictation.',
    category: 'Settings',
  },
  {
    filename: 'audio-input',
    slug: 'audio-input',
    title: 'Audio Input',
    subtitle: 'Choose the right microphone and input settings.',
    category: 'Settings',
  },
  {
    filename: 'recording-feedback',
    slug: 'recording-feedback',
    title: 'Recording Feedback',
    subtitle: 'Control how VoiceInk shows recording status.',
    category: 'Settings',
  },
  {
    filename: 'settings-general',
    slug: 'settings-general',
    title: 'General Settings',
    subtitle: 'Tune the everyday behavior of VoiceInk.',
    category: 'Settings',
  },
  {
    filename: 'transcription-models',
    slug: 'transcription-models',
    title: 'Transcription Models',
    subtitle: 'Understand local and cloud transcription options.',
    category: 'Transcription',
  },
  {
    filename: 'transcription-history',
    slug: 'transcription-history',
    title: 'Transcription History',
    subtitle: 'Review, reuse, and analyze previous transcripts.',
    category: 'Transcription',
  },
  {
    filename: 'transcribe-audio-files',
    slug: 'transcribe-audio-files',
    title: 'Transcribe Audio Files',
    subtitle: 'Convert existing recordings into text.',
    category: 'Transcription',
  },
  {
    filename: 'custom-local-whisper-models',
    slug: 'custom-local-whisper-models',
    title: 'Custom Local Whisper Models',
    subtitle: 'Use your own local Whisper models with VoiceInk.',
    category: 'Transcription',
  },
  {
    filename: 'enhancements-configuring-models',
    slug: 'enhancements-configuring-models',
    title: 'Configuring Enhancement Models',
    subtitle: 'Set providers and models for text enhancement.',
    category: 'Enhancements',
  },
  {
    filename: 'enhancements-trigger-words',
    slug: 'enhancements-trigger-words',
    title: 'Enhancement Trigger Words',
    subtitle: 'Use spoken triggers to activate the right prompt.',
    category: 'Enhancements',
  },
  {
    filename: 'enhancements-assistant-mode',
    slug: 'enhancements-assistant-mode',
    title: 'AI Assistant Mode',
    subtitle: 'Use VoiceInk as an assistant for longer tasks.',
    category: 'Enhancements',
  },
  {
    filename: 'power-mode',
    slug: 'power-mode',
    title: 'Power Mode',
    subtitle: 'Apply saved configurations for apps and websites.',
    category: 'Power Mode',
  },
  {
    filename: 'switching-power-modes',
    slug: 'switching-power-modes',
    title: 'Switching Power Modes',
    subtitle: 'Move between saved power mode configurations.',
    category: 'Power Mode',
  },
  {
    filename: 'switching-enhancement-prompts',
    slug: 'switching-enhancement-prompts',
    title: 'Switching Enhancement Prompts',
    subtitle: 'Change prompts while you write.',
    category: 'Power Mode',
  },
  {
    filename: 'contextual-awareness',
    slug: 'contextual-awareness',
    title: 'Contextual Awareness',
    subtitle: 'Let VoiceInk react to where you are writing.',
    category: 'Power Mode',
  },
  {
    filename: 'word-replacements',
    slug: 'word-replacements',
    title: 'Word Replacements',
    subtitle: 'Replace phrases and names automatically.',
    category: 'Dictionary',
  },
  {
    filename: 'correct-spellings',
    slug: 'correct-spellings',
    title: 'Correct Spellings',
    subtitle: 'Teach VoiceInk the spelling you prefer.',
    category: 'Dictionary',
  },
  {
    filename: 'filler-words',
    slug: 'filler-words',
    title: 'Filler Words',
    subtitle: 'Control cleanup for ums, ahs, and repeated words.',
    category: 'Dictionary',
  },
  {
    filename: 'common-issues',
    slug: 'common-issues',
    title: 'Common Issues',
    subtitle: 'Solutions to frequently encountered problems with VoiceInk.',
    category: 'Common Issues',
  },
  {
    filename: 'transcription-taking-too-long',
    slug: 'transcription-taking-too-long',
    title: 'Transcription Taking Too Long',
    subtitle: 'Improve slow transcription performance.',
    category: 'Common Issues',
  },
  {
    filename: 'model-or-language-changing-automatically',
    slug: 'model-or-language-changing-automatically',
    title: 'Model or Language Changing Automatically',
    subtitle: 'Fix unexpected model or language changes.',
    category: 'Common Issues',
  },
  {
    filename: 'repeated-text-or-hallucinations',
    slug: 'repeated-text-or-hallucinations',
    title: 'Repeated Text or Hallucinations',
    subtitle: 'Reduce repeated words and inaccurate output.',
    category: 'Common Issues',
  },
  {
    filename: 'transcription-failing',
    slug: 'transcription-failing',
    title: 'Transcription Failing',
    subtitle: 'Recover when transcription does not complete.',
    category: 'Common Issues',
  },
  {
    filename: 'clipboard-issues',
    slug: 'clipboard-issues',
    title: 'Clipboard Issues',
    subtitle: 'Fix copy, paste, and clipboard permission problems.',
    category: 'Common Issues',
  },
  {
    filename: 'ios-macos-license-confusion',
    slug: 'ios-macos-license-confusion',
    title: 'iOS and macOS License Confusion',
    subtitle: 'Understand license differences across platforms.',
    category: 'Common Issues',
  },
];

export const searchableDocs = docsRegistry.filter((doc) => doc.filename && doc.searchable !== false);

export const getDocBySlug = (slug?: string) => docsRegistry.find((doc) => doc.slug === slug);

export const getCategorySlug = (category: string) => category.toLowerCase().replace(/\s+/g, '-');

export const formatCategoryName = (categorySlug?: string) => {
  if (!categorySlug) return '';
  return categorySlug.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatArticleCount = (count: number) => `${count} ${count === 1 ? 'article' : 'articles'}`;

export const getDocsByCategory = (category: string) => docsRegistry.filter((doc) => doc.category === category);

export const getGroupedDocs = () =>
  categoryOrder.reduce<Record<string, DocMeta[]>>((groups, category) => {
    const docs = getDocsByCategory(category);
    if (docs.length) groups[category] = docs;
    return groups;
  }, {});
